import { useEffect } from 'react'
import { App } from '@capacitor/app'
import { nativeQueueManager } from './nativeQueueManager'
import { useSyncQueueStore } from '@/lib/helpers/bgSync/useSyncQueueStore'
import { toastFromQueueError } from '@/lib/helpers/toastFromQueueError'
import type { Metadata } from '@/lib/types/bgSyncTypes'
import { shouldUseNativeQueue } from '@/lib/platform/platformDetection'
import { SYNC_RETRY_INTERVAL_MS, SYNC_DEBOUNCE_MS } from './syncConstants'
import { probeConnectivity, invalidateProbeCache } from '@/lib/connectivityProbe'
import { queryClient } from '@/lib/api-client'
// `addNetworkListener` routes through the dev-harness wrapper so flipping
// NetworkPanel between online/offline fires `networkStatusChange` here
// and the queue auto-sync reacts to harness-driven transitions just like
// real device events. In production this degenerates (via DCE) to a
// direct `Network.addListener` call.
import { addListener as addNetworkListener } from '@/lib/platform/networkStatus'

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[NativeQueueSync]', ...args)

/**
 * Create a debounced version of a function
 */
function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number,
): (...args: TArgs) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  return (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delayMs)
  }
}

export function useNativeQueueAutoSync(): void {
  const { readData } = useSyncQueueStore()
  const isNative = shouldUseNativeQueue()
  // `errMessage`/`useTranslation` were the legacy fallback when
  // `meta.error` was missing. `toastFromQueueError` now owns that
  // fallback (via i18n) so it can pick the right message per endpoint +
  // status. No `useTranslation` needed here anymore.

  useEffect(() => {
    const handleFailure = (meta: Metadata) => {
      // Friendly, status-aware toast — replaces the previous raw
      // "🚨 Coś poszło nie tak / HTTP 403: Forbidden" pair that testers
      // flagged as two confusing toasts in a row. See toastFromQueueError
      // for the full mapping.
      toastFromQueueError(meta)
    }

    // Sync success is normally handled by Mercure SSE (works on iOS via JWT auth).
    // Comment creates can race route remounts while the exact query is active, so
    // refresh the narrow comment/event keys without invalidating the whole app.
    const handleSuccess = (meta: Metadata) => {
      debug('Sync success for type:', meta.type)
      if (meta.type === 'comments') {
        void queryClient.refetchQueries({ queryKey: ['singleIssueComments'], type: 'active' })
        void queryClient.refetchQueries({ queryKey: ['singleIssueEvents'], type: 'active' })
      }
    }

    const unsubscribeFailure = nativeQueueManager.addFailureListener(handleFailure)
    const unsubscribeSuccess = nativeQueueManager.addSuccessListener(handleSuccess)

    return () => {
      unsubscribeFailure()
      unsubscribeSuccess()
    }
    // Failure mapping is driven by i18n.t() inside the helper; locale
    // changes are picked up at toast-fire time, so no React dep is
    // needed here.
  }, [])

  useEffect(() => {
    // `readData()` is a full Dexie `toArray()` + a `JSON.parse` per body +
    // a Zustand `set()` that re-renders every subscriber (Kolejka table, the
    // sidebar count, the drain invalidator, etc). A 90-item bulk fires
    // `notifyListeners` ~180 times (enqueue + drain per item); doing the
    // read synchronously per fire is why Kolejka was crawling. Coalesce to
    // ~10 reads/sec while still keeping the UI lively.
    const debouncedRead = debounce(() => {
      readData()
    }, 100)
    const unsubscribe = nativeQueueManager.addListener(debouncedRead)

    return () => {
      unsubscribe()
    }
  }, [readData])

  useEffect(() => {
    const doSync = async (reason: string) => {
      debug(`Triggering sync: ${reason}`)
      await readData()

      // Verify actual server reachability — navigator.onLine is unreliable
      // (captive portals, dead WiFi). The probe does a lightweight HEAD to
      // /version.json with a 5s timeout and caches the result for 5s.
      const connected = await probeConnectivity()

      if (connected) {
        await nativeQueueManager.performSync()
      } else {
        debug('Offline, skipping sync')
      }
    }

    // Debounce sync triggers to prevent rapid-fire syncs when multiple events fire
    // (e.g., focus + online + visible all fire on app resume)
    const triggerSync = debounce((reason: string) => {
      doSync(reason).catch(err => {
        debug('Sync failed:', err)
      })
    }, SYNC_DEBOUNCE_MS)

    let handles: {
      networkHandle: { remove: () => void }
      appStateHandle: { remove: () => void }
    } | null = null
    let mounted = true

    const setupListeners = async () => {
      try {
        const networkHandle = await addNetworkListener('networkStatusChange', status => {
          debug('Network status changed:', status)
          if (status.connected) {
            invalidateProbeCache()
            triggerSync('network-restored')
          }
        })

        const appStateHandle = await App.addListener('appStateChange', state => {
          debug('App state changed:', state)
          if (state.isActive) {
            triggerSync('app-resumed')
          }
        })

        return { networkHandle, appStateHandle }
      } catch (error) {
        console.error('[NativeQueueSync] Failed to setup Capacitor listeners:', error)
        return null
      }
    }

    if (isNative) {
      setupListeners()
        .then(h => {
          if (mounted && h) {
            handles = h
          } else if (!mounted && h) {
            h.networkHandle.remove()
            h.appStateHandle.remove()
          }
        })
        .catch(err => {
          debug('Failed to setup listeners:', err)
        })
    }

    const onFocus = () => triggerSync('focus')
    const onOnline = () => {
      // Network just came back — invalidate the cached probe result so
      // the next doSync does a real connectivity check.
      invalidateProbeCache()
      triggerSync('online')
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') triggerSync('visible')
    }
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) triggerSync('pageshow')
    }

    const initialSyncTimeout = setTimeout(() => {
      triggerSync('mount')
    }, 200)

    window.addEventListener('focus', onFocus)
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onPageShow)

    const interval = setInterval(() => {
      triggerSync('interval')
    }, SYNC_RETRY_INTERVAL_MS)

    return () => {
      mounted = false
      if (handles) {
        handles.networkHandle.remove()
        handles.appStateHandle.remove()
      }
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onPageShow)
      clearInterval(interval)
      clearTimeout(initialSyncTimeout)
    }
  }, [readData, isNative])
}
